import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { mintSingleton } from 'src/client/mint';
import { FREESTAR_PRIMIS_VIDEO_RELOAD_ON_ROUTE_ENABLED } from 'src/common/constants/experiments/ads';
import useExperiment from 'src/common/hooks/useExperiment';
import useOnMount from 'src/common/hooks/useOnMount';
import usePrevious from 'src/common/hooks/usePrevious';
import { useStores } from 'src/common/providers';
import { selectIsNowPlayingDialogOpen } from 'src/common/selectors/dialog';

const placementName = 'FreeStarVideoAdContainer';

export default function FreeStarVideoAdContainer({ isInNowPlayingDialog = false }) {
  const isNowPlayingDialogOpen = useSelector(selectIsNowPlayingDialogOpen);
  const wasNowPlayingDialogOpen = usePrevious(isNowPlayingDialogOpen);
  const history = useHistory();
  const previousLocationPathname = usePrevious(history.location.pathname);
  const isFreestarPrimisVideoReloadOnRouteEnabled = useExperiment(
    FREESTAR_PRIMIS_VIDEO_RELOAD_ON_ROUTE_ENABLED,
  );
  const { playerStore } = useStores();
  const isOutstreamVideoEnabled = playerStore.metadata()?.ads?.canShowNowPlayingMrecVideoAds;
  const didRequestVideoAd = useRef(false);

  const reloadVideoAd = useCallback(() => {
    mintSingleton.instance?.deleteInContentVideo?.(placementName);
    mintSingleton.instance?.requestInContentVideo?.(placementName);
    didRequestVideoAd.current = true;
  }, []);

  useOnMount(() => {
    if (!isOutstreamVideoEnabled) {
      return;
    }

    async function onMount() {
      await mintSingleton.readyPromise;

      reloadVideoAd();
    }

    onMount();

    return async () => {
      await mintSingleton.readyPromise;

      mintSingleton.instance?.deleteInContentVideo?.(placementName);
    };
  });

  useEffect(() => {
    if (!isFreestarPrimisVideoReloadOnRouteEnabled || !isOutstreamVideoEnabled) {
      return;
    }

    if (!!history.location.pathname && history.location.pathname !== previousLocationPathname) {
      reloadVideoAd();
    }
  }, [
    history.location.pathname,
    previousLocationPathname,
    isFreestarPrimisVideoReloadOnRouteEnabled,
    reloadVideoAd,
    isOutstreamVideoEnabled,
  ]);

  useEffect(() => {
    if (!isInNowPlayingDialog || !isOutstreamVideoEnabled) {
      return;
    }

    if (!wasNowPlayingDialogOpen && isNowPlayingDialogOpen) {
      reloadVideoAd();
    }
  }, [
    isInNowPlayingDialog,
    wasNowPlayingDialogOpen,
    isNowPlayingDialogOpen,
    reloadVideoAd,
    isOutstreamVideoEnabled,
  ]);

  useEffect(() => {
    // Disable outstream when outstream enablement flag changes to false
    if (!isOutstreamVideoEnabled) {
      mintSingleton.readyPromise?.then(() => {
        mintSingleton.instance?.deleteInContentVideo?.(placementName);
        didRequestVideoAd.current = false;
      });
      return;
    }

    // Enable outstream when outstream enablement flag changes to true and a request hasn't been made yet
    if (isOutstreamVideoEnabled) {
      mintSingleton.readyPromise?.then(() => {
        if (!didRequestVideoAd.current) {
          reloadVideoAd();
        }
      });
    }
  }, [isOutstreamVideoEnabled, reloadVideoAd]);

  return (
    <div id="FreeStarVideoAdContainer" style={{ marginTop: '10px', marginBottom: '10px' }}>
      <div id="freestar-video-parent">
        <div id="freestar-video-child" />
      </div>
    </div>
  );
}
